import { useEffect, lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { AMBASSADOR_VISIBLE } from "@/lib/challenge";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { captureRef } from "@/lib/appresse";
import { DownloadInfoModal } from "@/components/DownloadInfoModal";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";

// Heavy / secondary routes are code-split so the first paint (home) only
// downloads & parses the minimum JS — critical on old / low-end phones.
const Explorer = lazy(() => import("@/pages/Explorer"));
const Favoris = lazy(() => import("@/pages/Favoris"));
const MesRevues = lazy(() => import("@/pages/MesRevues"));
const Compte = lazy(() => import("@/pages/Compte"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Ambassadeur = lazy(() => import("@/pages/Ambassadeur"));
const Admin = lazy(() => import("@/pages/Admin"));
const Partage = lazy(() => import("@/pages/Partage"));
const PartnerPortal = lazy(() => import("@/pages/PartnerPortal"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const HorsLigne = lazy(() => import("@/pages/HorsLigne"));
const Abonnement = lazy(() => import("@/pages/Abonnement"));
const PointsCapital = lazy(() => import("@/pages/PointsCapital"));
const Rejoindre = lazy(() => import("@/pages/Rejoindre"));
const PaymentReturn = lazy(() => import("@/pages/PaymentReturn"));

function App() {
  useEffect(() => {
    const saved = localStorage.getItem("appresse_theme") || "light";
    document.documentElement.classList.toggle("dark", saved === "dark");
    captureRef();
    // Warm up the connection to the API/image origin so the first covers paint
    // as fast as possible — same benefit on phones, tablets, laptops & desktops.
    try {
      const origin = process.env.REACT_APP_BACKEND_URL;
      if (origin) {
        ["preconnect", "dns-prefetch"].forEach((rel) => {
          const l = document.createElement("link");
          l.rel = rel;
          l.href = origin;
          l.crossOrigin = "anonymous";
          document.head.appendChild(l);
        });
      }
    } catch { /* ignore */ }
  }, []);

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/explorer" element={<Explorer />} />
                <Route path="/mes-revues" element={<MesRevues />} />
                <Route path="/favoris" element={<Favoris />} />
                <Route path="/hors-ligne" element={<HorsLigne />} />
                <Route path="/compte" element={<Compte />} />
                <Route path="/confidentialite" element={<LegalPage />} />
                <Route path="/mentions-legales" element={<LegalPage />} />
                <Route path="/compte/abonnement" element={<Abonnement />} />
                <Route path="/compte/points" element={<PointsCapital />} />
                <Route path="/ambassadeur" element={AMBASSADOR_VISIBLE ? <Ambassadeur /> : <Navigate to="/" replace />} />
              </Route>
              <Route path="/admin" element={<Admin />} />
              <Route path="/rejoindre/:token" element={<Rejoindre />} />
              <Route path="/payment/success" element={<PaymentReturn mode="success" />} />
              <Route path="/payment/cancel" element={<PaymentReturn mode="cancel" />} />
              <Route path="/partenaire" element={<PartnerPortal />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/partage" element={<Partage />} />
              <Route path="/share-target" element={<Partage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
        <DownloadInfoModal />
      </AuthProvider>
    </div>
  );
}

export default App;
